<script setup>
import {
  inject,
  onMounted,
  onUnmounted,
  ref,
  watch,
  provide,
  nextTick,
} from 'vue';
import MapLibreGL from 'maplibre-gl';

const props = defineProps({
  longitude: {
    type: Number,
    required: true,
  },
  latitude: {
    type: Number,
    required: true,
  },
  draggable: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: '#3B82F6',
  },
  options: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['click', 'dragstart', 'drag', 'dragend']);

const { map, isLoaded } = inject('mapContext');
const markerInstance = ref(null);
const markerElement = ref(null);
const customContentRef = ref(null);

provide('markerContext', {
  marker: markerInstance,
});

const createMarker = async () => {
  // eslint-disable-next-line no-console
  console.log('[MapMarker] createMarker called', {
    hasMap: !!map.value,
    isLoaded: isLoaded.value,
    hasMarker: !!markerInstance.value,
    lat: props.latitude,
    lng: props.longitude,
    color: props.color,
  });

  if (!map.value || !isLoaded.value) {
    // eslint-disable-next-line no-console
    console.log('[MapMarker] Skipping - map or isLoaded not ready');
    return;
  }
  if (markerInstance.value) {
    // eslint-disable-next-line no-console
    console.log('[MapMarker] Skipping - marker already exists');
    return;
  }

  await nextTick();

  let element;

  if (customContentRef.value && customContentRef.value.children.length > 0) {
    // eslint-disable-next-line no-console
    console.log('[MapMarker] Using custom slot content from template');
    element = customContentRef.value.firstElementChild;
  } else {
    // Créer l'élément du marqueur par défaut
    element = document.createElement('div');
    element.className = 'custom-mapcn-marker';

    // Styles essentiels pour la visibilité
    element.style.cssText = `
      cursor: pointer;
      position: relative;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      pointer-events: auto;
    `;

    // Créer le contenu du marqueur
    const iconContent = document.createElement('div');

    iconContent.style.cssText = `
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: ${props.color};
      border: 2px solid white;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      cursor: pointer;
      transition: transform 0.2s;
      position: relative;
      z-index: 1001;
      pointer-events: auto;
    `;

    // Effet hover
    element.addEventListener('mouseenter', () => {
      iconContent.style.transform = 'scale(1.25)';
    });
    element.addEventListener('mouseleave', () => {
      iconContent.style.transform = 'scale(1)';
    });

    element.appendChild(iconContent);
  }

  markerElement.value = element;

  // Créer le marqueur MapLibre GL directement
  const marker = new MapLibreGL.Marker({
    ...props.options,
    element,
    draggable: props.draggable,
  })
    .setLngLat([props.longitude, props.latitude])
    .addTo(map.value);

  element.addEventListener('click', e => {
    emit('click', e);
  });

  marker.on('dragstart', () => emit('dragstart', marker.getLngLat()));
  marker.on('drag', () => emit('drag', marker.getLngLat()));
  marker.on('dragend', () => emit('dragend', marker.getLngLat()));

  markerInstance.value = marker;

  // eslint-disable-next-line no-console
  console.log('[MapMarker] Marker created and added to map', {
    marker,
    element: element,
    position: marker.getLngLat(),
    elementVisible: element.offsetWidth > 0 && element.offsetHeight > 0,
  });
};

onMounted(async () => {
  await nextTick();

  if (isLoaded.value) {
    createMarker();
  } else {
    const stopWatcher = watch(
      isLoaded,
      async loaded => {
        if (loaded) {
          await nextTick();
          createMarker();
          stopWatcher();
        }
      },
      { immediate: true }
    );
  }
});

onUnmounted(() => {
  if (markerInstance.value) {
    markerInstance.value.remove();
    markerInstance.value = null;
  }
});

watch(
  () => [props.longitude, props.latitude],
  ([newLng, newLat]) => {
    if (markerInstance.value) {
      markerInstance.value.setLngLat([newLng, newLat]);
    }
  }
);

// Mettre à jour la couleur du marqueur si elle change
watch(
  () => props.color,
  newColor => {
    if (markerElement.value) {
      const iconContent = markerElement.value.querySelector('div');
      if (iconContent) {
        iconContent.style.backgroundColor = newColor;
      }
    }
  }
);
</script>

<template>
  <!-- Conteneur caché pour capturer le contenu du slot et l'utiliser comme élément de marqueur -->
  <div ref="customContentRef" class="hidden">
    <slot />
  </div>
  <!-- On garde un slot ici pour que les composants enfants comme MapPopup puissent être montés et injectés -->
  <slot v-if="markerInstance" />
</template>

<style>
/* Styles globaux pour les marqueurs MapLibre GL */
.custom-mapcn-marker {
  position: relative !important;
  z-index: 1000 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  pointer-events: auto !important;
  width: 20px !important;
  height: 20px !important;
}

.custom-mapcn-marker > div {
  position: relative !important;
  z-index: 1001 !important;
  pointer-events: auto !important;
  width: 16px !important;
  height: 16px !important;
  border-radius: 50% !important;
}
</style>
